// ApexCharts option builders for the dashboard. Charts share one look; each builder takes only
// what differs between charts (categories, colours, sizes).
import { compactCurrency, formatCurrency, shortLabel } from "@/utils/formatters";

const AXIS_TEXT = "#64748b";
const GRID_BORDER = "#e8edf3";
const GRADIENT_FILL = {
  type: "gradient",
  gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05, stops: [0, 90, 100] },
};
const TOP_LEGEND = { show: true, position: "top", horizontalAlign: "right", fontWeight: 700, markers: { radius: 12 } };

const baseChart = () => ({ toolbar: { show: false }, parentHeightOffset: 0 });
const currencyTooltip = () => ({ y: { formatter: (value) => formatCurrency(value) } });
const compactCurrencyYAxis = () => ({
  labels: { formatter: (value) => compactCurrency(value), style: { colors: AXIS_TEXT, fontSize: "11px" } },
});

// Month axis. Year-long (12 month) charts tilt labels and pad the grid so they fit.
function monthAxis(categories, yearLong) {
  const style = { colors: AXIS_TEXT, fontSize: "11px", fontWeight: 600 };
  const labels = yearLong ? { rotate: -20, trim: true, hideOverlappingLabels: true, style } : { style };
  return { categories, labels };
}

function grid(yearLong) {
  return yearLong
    ? { borderColor: GRID_BORDER, strokeDashArray: 4, padding: { left: 4, right: 10, bottom: 8 } }
    : { borderColor: GRID_BORDER, strokeDashArray: 4 };
}

export function columnChartOptions({ categories, colors, columnWidth, borderRadius = 6, yearLong = false, legend = false }) {
  return {
    chart: baseChart(),
    colors,
    dataLabels: { enabled: false },
    stroke: { width: 0 },
    xaxis: monthAxis(categories, yearLong),
    yaxis: compactCurrencyYAxis(),
    plotOptions: { bar: { borderRadius, borderRadiusApplication: "end", columnWidth } },
    grid: grid(yearLong),
    legend: legend ? TOP_LEGEND : { show: false },
    tooltip: currencyTooltip(),
  };
}

export function areaChartOptions({ categories, color, yearLong = false }) {
  return {
    chart: baseChart(),
    colors: [color],
    dataLabels: { enabled: false },
    stroke: { width: 3, curve: "smooth" },
    fill: GRADIENT_FILL,
    xaxis: monthAxis(categories, yearLong),
    yaxis: compactCurrencyYAxis(),
    grid: grid(yearLong),
    legend: { show: false },
    tooltip: currencyTooltip(),
  };
}

// Horizontal "top N items" bars, one colour per bar, value printed on the bar.
export function rankingBarChartOptions({ labels, colors }) {
  return {
    chart: baseChart(),
    colors,
    plotOptions: {
      bar: { horizontal: true, distributed: true, borderRadius: 7, borderRadiusApplication: "end", barHeight: "56%" },
    },
    dataLabels: {
      enabled: true,
      formatter: (value) => compactCurrency(value),
      style: { colors: ["#fff"], fontSize: "11px", fontWeight: 800 },
    },
    xaxis: {
      categories: labels.map((label) => shortLabel(label)),
      labels: { formatter: (value) => compactCurrency(value), style: { colors: AXIS_TEXT, fontSize: "11px" } },
    },
    yaxis: { labels: { style: { colors: "#475569", fontSize: "11px", fontWeight: 700 } } },
    grid: { borderColor: GRID_BORDER, strokeDashArray: 4, padding: { left: 8, right: 16 } },
    legend: { show: false },
    tooltip: currencyTooltip(),
  };
}

// Donut with a "Total" in the middle.
export function shareDonutChartOptions({ labels, series }) {
  const total = series.reduce((sum, value) => sum + Number(value || 0), 0);
  return {
    labels,
    chart: baseChart(),
    dataLabels: {
      enabled: true,
      formatter: (value) => `${Number(value || 0).toFixed(0)}%`,
      style: { fontSize: "11px", fontWeight: 800 },
      dropShadow: { enabled: false },
    },
    legend: { position: "bottom", fontWeight: 700, itemMargin: { horizontal: 8, vertical: 5 } },
    stroke: { colors: ["#fff"], width: 4 },
    plotOptions: {
      pie: {
        donut: {
          size: "64%",
          labels: {
            show: true,
            name: { show: true, fontSize: "12px", color: AXIS_TEXT },
            value: { show: true, fontSize: "20px", fontWeight: 800, formatter: (value) => compactCurrency(value) },
            total: { show: true, label: "Total", fontSize: "12px", color: AXIS_TEXT, formatter: () => compactCurrency(total) },
          },
        },
      },
    },
    colors: ["#2563eb", "#f97316", "#10b981", "#e11d48", "#8b5cf6", "#f59e0b", "#06b6d4"],
    tooltip: currencyTooltip(),
  };
}

// Stacked purchases/sales bars in the page's orange palette.
export function salesPurchaseChartOptions(months) {
  return {
    chart: { toolbar: { show: false }, stacked: true },
    colors: ["#fde3c5", "#f6a044"],
    dataLabels: { enabled: false },
    stroke: { width: 0 },
    xaxis: { categories: months, labels: { rotate: 0, style: { colors: "#586176", fontSize: "12px" } } },
    yaxis: { labels: { formatter: (value) => compactCurrency(value) } },
    plotOptions: { bar: { borderRadius: 10, columnWidth: "52%" } },
    grid: { borderColor: "#e7ebf0", strokeDashArray: 5 },
    legend: { show: false },
    tooltip: currencyTooltip(),
  };
}
